/* eslint-disable */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Modal, message } from "antd";
import moment from "moment";

import { IUseBeneficiaries } from "./IUseBeneficiaries";
import { Beneficiaries, BeneficiariesErrors, InvestorDTO } from "../../Models";
import { emptyBeneficiary, emptyBeneficiaryErrors } from "../../Assets/Data/EmptyValues";
import Utils from "../../Utils";

export const useBeneficiaries = ({
	translations,
	investorAPI,
	beneficiaryAPI,
	contractAPI,
	placesService,
	unlockNextStep,
	getPlacePredictions,
	mode = 'full'
}: IUseBeneficiaries) => {
	const [searchParams, _] = useSearchParams();
	const MIN_PARTICIPATION_LITE = 5;
	const MIN_PARTICIPATION_FULL = 25;
	const isLiteMode = mode === 'lite';

	const [loading, setLoading] = useState<boolean>(false);
	const [contractId, setContractId] = useState<number>(0);
	const [beneficiaries, setBeneficiaries] = useState<Beneficiaries[]>([{...emptyBeneficiary}]);
	const [nationalitiesList, setNationalitiesList] = useState<any[]>([]);
	const [errors, setErrors] = useState<BeneficiariesErrors[]>([{ ...emptyBeneficiaryErrors }]);
	const [contractLanguage, setContractLanguage] = useState<string>('es');
	const [taxAddressInputs, setTaxAddressInputs] = useState<string[]>([]);
	const [prefillOptions, setPrefillOptions] = useState<Array<{ value: number; label: string; investor: InvestorDTO }>>([]);
	const [manualAddressVisibility, setManualAddressVisibility] = useState<Record<number, { taxResidence: boolean }>>({});

	const [activeAddress, setActiveAddress] = useState<{ index: number, field: string } | null>(null);

	const updateManualAddressVisibility = (
		index: number,
		field: 'taxResidence',
		visible: boolean,
	) => {
		void field;
		setManualAddressVisibility((prevState) => ({
			...prevState,
			[index]: {
				taxResidence: visible,
			},
		}));
	};

	const parseParticipationPercentage = (value: unknown): number => {
		if (typeof value === 'number') {
			return Number.isFinite(value) ? value : 0;
		}
		if (typeof value === 'string') {
			const normalizedValue = value.trim().replace(',', '.');
			const parsedValue = Number(normalizedValue);
			return Number.isFinite(parsedValue) ? parsedValue : 0;
		}
		return 0;
	};

	const normalizeAllowedDocumentType = (documentType?: string) => {
		const normalizedValue = (documentType || '').trim().toLowerCase();
		if (normalizedValue === 'dni') return 'dni';
		if (normalizedValue === 'nif') return 'nif';
		if (normalizedValue === 'passport' || normalizedValue === 'pasaporte') return 'passport';
		return '';
	};

	const getInvestorDocumentType = (investor: InvestorDTO) => {
		const normalizedType = normalizeAllowedDocumentType(investor.documentType);
		if (normalizedType) {
			return normalizedType;
		}
		if (investor.nif) {
			return 'nif';
		}
		if (investor.dni) {
			return 'dni';
		}
		if (investor.passport) {
			return 'passport';
		}
		return '';
	};

	const getInvestorFullName = (investor?: InvestorDTO) => [investor?.name, investor?.lastName, investor?.secondLastName]
		.filter(Boolean)
		.join(' ')
		.trim()
		.toLowerCase();

	const getBeneficiaryFullName = (beneficiary?: Beneficiaries) => [beneficiary?.name, beneficiary?.lastName, beneficiary?.secondLastName]
		.filter(Boolean)
		.join(' ')
		.trim()
		.toLowerCase();

	const buildBeneficiaryFromInvestor = (investor: InvestorDTO): Beneficiaries => ({
		...emptyBeneficiary,
		name: investor.name || '',
		lastName: investor.lastName || '',
		secondLastName: investor.secondLastName || '',
		birthDate: investor.birthDate || '',
		nationality: investor.nationality || '',
		email: investor.emailContact || '',
		documentType: getInvestorDocumentType(investor),
		nif: investor.document || investor.nif || investor.dni || investor.passport || '',
		documentExpiration: investor.docValidThru || '',
		taxResidence: investor.mainResidence || '',
		taxResidenceCity: investor.mainResidenceCity || '',
		taxResidencePostalCode: investor.mainResidencePostalCode || '',
		countryTaxResidence: investor.mainResidenceCountry || '',
		taxResidenceExtra: investor.mainResidenceExtra || '',
	});

	const mergeBeneficiaryWithInvestorData = (beneficiary: Beneficiaries, investor?: InvestorDTO): Beneficiaries => {
		if (!investor) {
			return beneficiary;
		}

		const prefilledBeneficiary = buildBeneficiaryFromInvestor(investor);
		return {
			...prefilledBeneficiary,
			...beneficiary,
			name: beneficiary.name || prefilledBeneficiary.name,
			lastName: beneficiary.lastName || prefilledBeneficiary.lastName,
			secondLastName: beneficiary.secondLastName || prefilledBeneficiary.secondLastName,
			birthDate: beneficiary.birthDate || prefilledBeneficiary.birthDate,
			nationality: beneficiary.nationality || prefilledBeneficiary.nationality,
			email: beneficiary.email || prefilledBeneficiary.email,
			documentType: beneficiary.documentType || prefilledBeneficiary.documentType,
			nif: beneficiary.nif || prefilledBeneficiary.nif,
			documentExpiration: beneficiary.documentExpiration || prefilledBeneficiary.documentExpiration,
			taxResidence: beneficiary.taxResidence || prefilledBeneficiary.taxResidence,
			taxResidenceCity: beneficiary.taxResidenceCity || prefilledBeneficiary.taxResidenceCity,
			taxResidencePostalCode: beneficiary.taxResidencePostalCode || prefilledBeneficiary.taxResidencePostalCode,
			countryTaxResidence: beneficiary.countryTaxResidence || prefilledBeneficiary.countryTaxResidence,
			taxResidenceExtra: beneficiary.taxResidenceExtra || prefilledBeneficiary.taxResidenceExtra,
		};
	};

	const normalizeDateFromApi = (value?: string) => {
		if (!value || typeof value !== 'string') {
			return '';
		}

		const trimmedValue = value.trim();
		if (!trimmedValue || trimmedValue.startsWith('0001-01-01')) {
			return '';
		}

		const parsedDate = moment(trimmedValue, [moment.ISO_8601, 'YYYY-MM-DD', 'DD/MM/YYYY'], true);
		return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : '';
	};

	useEffect(() => {
		if (searchParams.has('ContractId')) {
			const contractId = Number(searchParams.get('ContractId'));
			contractAPI.getContract(contractId).then((res: any) => {
				if(res.success){
					if(res.data?.idLanguage){
						setContractLanguage(res.data.idLanguage === 1 ? 'es' : 'en');
					}
				}
			});
			configureInitialData(contractId);
			setContractId(contractId);
		}
		else if (searchParams.has('hash')) {
			const hash = searchParams.get('hash');
			const getContractIdByHash = async(hash: string) => {
				return await contractAPI.getContractByHash(hash).then((res: any) => {
					if (res.success) {
						configureInitialData(Number(res.data));
						setContractId(Number(res.data));
						contractAPI.getContract(Number(res.data)).then((res: any) => {
							if(res.success){
								if(res.data?.idLanguage){
									setContractLanguage(res.data.idLanguage === 1 ? 'es' : 'en');
								}
							}
						});
					} else {
						message.error(translations.messageError);
					}
				})
				.catch(() => {
					message.error(translations.messageError);
				})
			}
			getContractIdByHash(hash!);
		}
		getNationalities();
	}, [translations]);

	const getNationalities = () => {
		investorAPI.getCountries().then((res: any) => {
			if (res !== "") {
				setNationalitiesList(res);
			}
			setLoading(false);
		})
		.catch((err: any) => {
			console.log(err);
		});
	};

	const configureInitialData = async(ContractId: number) => {
		setLoading(true);
		let investorsList: InvestorDTO[] = [];
		await investorAPI.getInvestors(ContractId).then((res: any) => {
			if (res.success) {
				investorsList = res.data || [];
				setPrefillOptions(investorsList.map((investor: InvestorDTO, index: number) => ({
					value: index,
					label: [investor.name, investor.lastName, investor.secondLastName].filter(Boolean).join(' ').trim(),
					investor,
				})).filter((option: any) => option.label));
			}
		}).catch((err: any) => console.log(err));

		await beneficiaryAPI.getBeneficiaries(ContractId).then((res: any) => {
			if(res.success){
				const configuredBeneficiaries: Beneficiaries[] = (res.data || []).map((beneficiary: Beneficiaries) => ({
					...beneficiary,
					birthDate: normalizeDateFromApi(beneficiary.birthDate),
					documentExpiration: normalizeDateFromApi(beneficiary.documentExpiration),
					documentType: normalizeAllowedDocumentType(beneficiary.documentType),
					hasPRP: Boolean(beneficiary.prp),
					isBeneficialOwner: typeof beneficiary.isBeneficialOwner === 'boolean'
						? beneficiary.isBeneficialOwner
						: parseParticipationPercentage(beneficiary.participationPercentage) > 0,
				})).map((beneficiary: Beneficiaries) => {
					const matchingInvestor = investorsList.find((investor: InvestorDTO) =>
						getBeneficiaryFullName(beneficiary) && getBeneficiaryFullName(beneficiary) === getInvestorFullName(investor)
					);
					return mergeBeneficiaryWithInvestorData(beneficiary, matchingInvestor);
				});

				const filteredBeneficiaries = configuredBeneficiaries.filter((beneficiary: Beneficiaries) => {
					const participationPercentage = parseParticipationPercentage(beneficiary.participationPercentage);
					return isLiteMode
						? participationPercentage >= MIN_PARTICIPATION_LITE
						: participationPercentage === 0
							|| participationPercentage >= MIN_PARTICIPATION_FULL;
				});
				

				if(filteredBeneficiaries.length > 0){
					setTaxAddressInputs(
						filteredBeneficiaries.map((beneficiary: Beneficiaries) =>
							beneficiary.taxResidence
								? `${beneficiary.taxResidence}, ${beneficiary.taxResidencePostalCode}, ${beneficiary.taxResidenceCity}, ${beneficiary.countryTaxResidence}`
								: ''
						)
					);
					setBeneficiaries(filteredBeneficiaries);
					setErrors(filteredBeneficiaries.map(() => ({...emptyBeneficiaryErrors})));
				}else{
					setBeneficiaries([{...emptyBeneficiary}]);
					setErrors([{...emptyBeneficiaryErrors}]);
					setTaxAddressInputs(['']);
				}
			}
			setLoading(false);
		}).catch((err: any) => {
			console.log(err);
			setLoading(false);
		})
	};

	const getInfo = () => {
		Modal.info({
			width: 700,
			content: (
				<div className="titles">
					{translations.tooltipPrpBeneficiario}
				</div>
			),
			onOk: () => { },
		});
	};

	const addRow = (investorIndex?: number) => {
		const selectedInvestor = investorIndex !== undefined
			? prefillOptions.find((option) => option.value === investorIndex)?.investor
			: undefined;
		const newElement = selectedInvestor ? buildBeneficiaryFromInvestor(selectedInvestor) : { ...emptyBeneficiary };

		setBeneficiaries(oldArray => [...oldArray, newElement]);
		setErrors(prevState => [...prevState, {...emptyBeneficiaryErrors}]);
		setTaxAddressInputs((prevState: string[]) => [
			...prevState,
			selectedInvestor
				? [selectedInvestor.mainResidence, selectedInvestor.mainResidencePostalCode, selectedInvestor.mainResidenceCity, selectedInvestor.mainResidenceCountry].filter(Boolean).join(', ')
				: ''
		]);
	};

	const validateData = (data: Beneficiaries, index: number) => {
		let isOK = true;
		const newErrors = [...errors];
		if (isLiteMode) {
			if (!data.name) {
				newErrors[index].name = true;
				isOK = false;
			} else if (!Utils.validate('name', data.name)) {
				newErrors[index].invalidNameFormat = true;
				isOK = false;
			}
			if (!data.lastName) {
				newErrors[index].lastName = true;
				isOK = false;
			} else if (!Utils.validate('name', data.lastName)) {
				newErrors[index].invalidLastNameFormat = true;
				isOK = false;
			}
			if (!data.secondLastName && data.nationality === 'Portugal') {
				newErrors[index].secondLastName = true;
				isOK = false;
			}
			if (data.secondLastName && !Utils.validate('name', data.secondLastName)) {
				newErrors[index].invalidSecondLastNameFormat = true;
				isOK = false;
			}
			if (!data.nationality) {
				newErrors[index].nationality = true;
				isOK = false;
			}

			const participationPercentage = Number(data.participationPercentage || 0);
			if (!participationPercentage) {
				newErrors[index].participationPercentage = true;
				isOK = false;
			} else if (participationPercentage < MIN_PARTICIPATION_LITE) {
				newErrors[index].percentageLesserThan25 = true;
				isOK = false;
			}

			setErrors(newErrors);
			return isOK;
		}

		if (!data.name) {
			newErrors[index].name = true;
			isOK = false;
		} else if (!Utils.validate('name', data.name)) {
			newErrors[index].invalidNameFormat = true;
			isOK = false;
		}
		if (!data.lastName) {
			newErrors[index].lastName = true;
			isOK = false;
		} else if (!Utils.validate('name', data.lastName)) {
			newErrors[index].invalidLastNameFormat = true;
			isOK = false;
		}
		if( !data.secondLastName && data.nationality === 'Portugal') {
			newErrors[index].secondLastName = true;
			isOK = false;
		}
		if (data.secondLastName && !Utils.validate('name', data.secondLastName)) {
			newErrors[index].invalidSecondLastNameFormat = true;
			isOK = false;
		}
		if (!data.birthDate) {
			newErrors[index].birthDate = true;
			isOK = false;
		}
		if (!data.nationality) {
			newErrors[index].nationality = true;
			isOK = false;
		}
		if (!data.fundsSource) {
			newErrors[index].source = true;
			isOK = false;
		} else if (data.fundsSource === 'Otro' && !data.otherFundsSource) {
			newErrors[index].otherSource = true;
			isOK = false;
		}
		if (!data.taxResidence || !data.taxResidenceCity || !data.taxResidencePostalCode || !data.countryTaxResidence) {
			newErrors[index].taxResidence = true;
			newErrors[index].taxResidenceCity = !data.taxResidenceCity;
			newErrors[index].taxResidencePostalCode = !data.taxResidencePostalCode;
			newErrors[index].countryTaxResidence = !data.countryTaxResidence;
			updateManualAddressVisibility(index, 'taxResidence', true);
			isOK = false;
		} else {
			updateManualAddressVisibility(index, 'taxResidence', false);
		}
		if (!data.nif) {
			newErrors[index].nif = true;
			isOK = false;
		} else if (!Utils.validate('document', data.nif!)) {
			newErrors[index].notValidDocument = true;
			isOK = false;
		}
		if (!data.documentExpiration) {
			newErrors[index].documentExpiration = true;
			isOK = false;
		} else {
			const expirationDate = moment(data.documentExpiration, ['YYYY-MM-DD', moment.ISO_8601], true);
			if (!expirationDate.isValid()) {
				newErrors[index].notValidExpirationDate = true;
				isOK = false;
			} else if (expirationDate.isBefore(moment(), 'day')) {
				newErrors[index].notValidExpirationDate = true;
				isOK = false;
			}
		}
		if (data.isBeneficialOwner && !data.participationPercentage) {
			newErrors[index].participationPercentage = true;
			isOK = false;
		}
		const participationPercentage = parseParticipationPercentage(data.participationPercentage);
		if (participationPercentage > 0 && participationPercentage < MIN_PARTICIPATION_FULL) {
			newErrors[index].percentageLesserThan25 = true;
			isOK = false;
		}
		if (data.hasPRP && !data.prp) {
			newErrors[index].prp = true;
			isOK = false;
		}
		if (!data.email) {
			newErrors[index].email = true;
			isOK = false;
		} else if (!Utils.validate('email', data.email)) {
			newErrors[index].notValidEmail = true;
			isOK = false;
		}
		if (!data.role) {
			newErrors[index].role = true;
			isOK = false;
		}
		setErrors(newErrors);
		return isOK;
	};

	const postBeneficiaries = async (newBeneficiaries: Beneficiaries[]) => {
		await beneficiaryAPI.saveBeneficiaries(newBeneficiaries)
			.then((res: any) => {
				if (res.success) {
					unlockNextStep(contractId);
					message.success(translations.datosGuardados);
				} else {
					message.error(translations.messageError);
				}
			});
	}

	const updateBeneficiaries = async (updatedBeneficiaries: Beneficiaries[], shouldUnlockStep: boolean = true) => {
		await beneficiaryAPI.updateBeneficiaries(updatedBeneficiaries)
		.then((res: any) => {
			if (res.success) {
				if (shouldUnlockStep) {
					unlockNextStep(contractId);
				}
				message.success(translations.datosGuardados);
			} else {
				message.error(translations.messageError);
			}
		});
	}

	const sanitizeBeneficiaryForApi = (beneficiary: Beneficiaries): Beneficiaries => {
		const payload: any = { ...beneficiary };
		const isValidDateValue = (value: any) => {
			if (!value || typeof value !== 'string') return false;
			return moment(value, ['YYYY-MM-DD', moment.ISO_8601], true).isValid();
		};

		if (!isValidDateValue(payload.documentExpiration)) {
			delete payload.documentExpiration;
		}

		return payload as Beneficiaries;
	};

	const saveData = async() => {
		if (beneficiaries.length > 0) {
			setLoading(true);
			let isDataOK = true;
			beneficiaries.map((beneficiary: Beneficiaries, i) => {
				const isDataValid = validateData(beneficiary, i);
				if (isDataOK && !isDataValid) {
					isDataOK = false;
				} else {
					beneficiary.participationPercentage = parseParticipationPercentage(beneficiary.participationPercentage);
					if (beneficiary.birthDate) {
						beneficiary.birthDate = moment(beneficiary.birthDate).format('YYYY-MM-DD');
					}
					if (beneficiary.documentExpiration) {
						beneficiary.documentExpiration = moment(beneficiary.documentExpiration).format('YYYY-MM-DD');
					}
					if (isLiteMode) {
						beneficiary.isBeneficialOwner = beneficiary.participationPercentage >= MIN_PARTICIPATION_FULL;
					}
					beneficiary.idContract = contractId;
				}

				return beneficiary;
			});
			const beneficiariesPayload = beneficiaries.map((beneficiary: Beneficiaries) => sanitizeBeneficiaryForApi(beneficiary));

			let newBeneficiaries = beneficiariesPayload.filter((x: any) => !x.id);
			let updatedBeneficiaries = beneficiariesPayload.filter((x: any) => x.id > 0);

			if (isDataOK) {
				if (newBeneficiaries.length > 0) {
					await postBeneficiaries(newBeneficiaries);
				}
				if(updatedBeneficiaries.length > 0){
					await updateBeneficiaries(updatedBeneficiaries, newBeneficiaries.length === 0);
				}
			} 
		}

		setLoading(false);
	};

	const deleteRow = async (i: number) => {
		if (beneficiaries[i].id > 0) {
			await beneficiaryAPI.deleteBeneficiary(beneficiaries[i].id)
				.then((res: any) => {
					if (res.success) {
						setBeneficiaries((prevState: any) => [...prevState.filter((_: any, index: number) => index !== i)]);
						setErrors(prevState => [...prevState.filter((_, index) => index !== i)]);
					} else {
						message.error(translations.messageError);
					}
				});
		} else {
			setBeneficiaries((prevState: any) => [...prevState.filter((_: any, index: number) => index !== i)]);
			setErrors(prevState => [...prevState.filter((_, index) => index !== i)]);
		}
	};

	const handleChangeData = (index: number) => (e: any) => {
		const { name, value } = e.target;

		setErrors((prevState: BeneficiariesErrors[]) => {
			const updatedErrors = [...prevState];
			updatedErrors[index || 0] = {
				...updatedErrors[index || 0],
				[name]: false,
				invalidNameFormat: name === 'name' ? false : updatedErrors[index || 0].invalidNameFormat,
				invalidLastNameFormat: name === 'lastName' ? false : updatedErrors[index || 0].invalidLastNameFormat,
				notValidDocument: name === 'nif' ? false : updatedErrors[index || 0].notValidDocument,
				notValidEmail: name === 'email' ? false : updatedErrors[index || 0].notValidEmail,
				notValidExpirationDate: name === 'documentExpiration' ? false : updatedErrors[index || 0].notValidExpirationDate,
				participationPercentage: name === 'participationPercentage' ? false : updatedErrors[index || 0].participationPercentage,
				percentageLesserThan25: name === 'participationPercentage' ? false : updatedErrors[index || 0].percentageLesserThan25,
			};
			return updatedErrors;
		});
		setBeneficiaries((prevState: Beneficiaries[]) => {
			const updatedInvestors = [...prevState];
			updatedInvestors[index] = {
				...updatedInvestors[index],
				[name]: value
			};
			return updatedInvestors;
		});
	};
	
	const handleSelectData = (field: string, index?: number) => (value: string) => {
		setErrors((prevState: any[]) => {
			const updatedErrors = [...prevState];
			updatedErrors[index || 0] = {
				...updatedErrors[index || 0],
				[field]: false,
				source: field === 'fundsSource' ? false : updatedErrors[index || 0].source,
			};
			return updatedErrors;
		});
		setBeneficiaries((prevState: Beneficiaries[]) => {
			const updatedInvestors = [...prevState];
			updatedInvestors[index || 0] = {
				...updatedInvestors[index || 0],
				[field]: value
			};
			return updatedInvestors;
		});
	};

	const handleChangeDate = (field: string, index: number) => (date: any) => {
		setBeneficiaries((prevState: Beneficiaries[]) => {
			const updatedInvestors = [...prevState];
			updatedInvestors[index] = {
				...updatedInvestors[index],
				[field]: date ? date.format('YYYY-MM-DD') : undefined
			};
			return updatedInvestors;
		});
		setErrors((prevState: any[]) => {
			const updatedErrors = [...prevState];
			updatedErrors[index || 0] = {
				...updatedErrors[index || 0],
				[field]: false
			};
			return updatedErrors;
		});
	};

	const getAddressComponent = (placeDetails: any, type: string) => {
		const component = placeDetails?.address_components?.find((component: any) => component.types.includes(type));
		return component ? component.long_name : "";
	};

	const getFormattedStreetAddress = (placeDetails: any, fallbackValue: string) => {
		const route = getAddressComponent(placeDetails, "route");
		const streetNumber = getAddressComponent(placeDetails, "street_number");
		const premise = getAddressComponent(placeDetails, "premise");
		const subpremise = getAddressComponent(placeDetails, "subpremise");
		const pointOfInterest = getAddressComponent(placeDetails, "point_of_interest");
		const neighborhood = getAddressComponent(placeDetails, "neighborhood");
		const placeName = placeDetails?.name?.trim?.() || "";
		const fallbackStreet = fallbackValue
			.split(",")[0]
			.split(" - ")[0]
			.trim();

		return [route, streetNumber].filter(Boolean).join(", ")
			|| premise
			|| subpremise
			|| placeName
			|| pointOfInterest
			|| neighborhood
			|| fallbackStreet;
	};

	const handleSelectAddress = (index: number, field: string) => (value: string, option: any) => {
		if (!activeAddress) return;
		placesService?.getDetails(
			{ placeId: option.key },
			(placeDetails: any) => {
				const address = getFormattedStreetAddress(placeDetails, value);
				const city = getAddressComponent(placeDetails, "locality") || getAddressComponent(placeDetails, "administrative_area_level_2");
				const postalCode = getAddressComponent(placeDetails, "postal_code");
				const country = getAddressComponent(placeDetails, "country");

				setBeneficiaries((prevState: Beneficiaries[]) => {
					const updatedInvestors = [...prevState];
					const updatedInvestor = { ...updatedInvestors[index] };

					if (field === "taxResidence") {
						setTaxAddressInputs((prevState: string[]) => {
							const updatedInputs = [...prevState];
							updatedInputs[index] = value;
							return updatedInputs;
						});
						updatedInvestor.taxResidence = address;
						updatedInvestor.taxResidenceCity = city;
						updatedInvestor.taxResidencePostalCode = postalCode;
						updatedInvestor.countryTaxResidence = country;
					}

					updatedInvestors[index] = updatedInvestor;
					return updatedInvestors;
				});
				setErrors((prevState: BeneficiariesErrors[]) => {
					const updatedErrors = [...prevState];
					updatedErrors[index] = {
						...updatedErrors[index],
						taxResidence: !address || !city || !postalCode || !country,
						taxResidenceCity: !city,
						taxResidencePostalCode: !postalCode,
						countryTaxResidence: !country,
					};
					return updatedErrors;
				});
				updateManualAddressVisibility(index, 'taxResidence', !address || !city || !postalCode || !country);
			}
		);
	};
	
	const handleSearchAddress = (index: number, field: string) => (value: string) => {
		setActiveAddress({ index, field });
		getPlacePredictions({ input: value });
	};

	const onChangeHasPRP = (index: number) => (checked: boolean) => {
		setBeneficiaries((prevState: Beneficiaries[]) => {
			const updatedBeneficiaries = [...prevState];
			updatedBeneficiaries[index] = {
				...updatedBeneficiaries[index],	
				hasPRP: checked,
				prp: checked ? updatedBeneficiaries[index].prp : ''
			};
			return updatedBeneficiaries;
		});
		setErrors((prevState: any[]) => {
			const updatedErrors = [...prevState];
			updatedErrors[index || 0] = {
				...updatedErrors[index || 0],
				prp: false
			};
			return updatedErrors;
		});
	};

	const onChangeIsBeneficialOwner = (index: number) => (checked: boolean) => {
		setBeneficiaries((prevState: Beneficiaries[]) => {
			const updatedBeneficiaries = [...prevState];
			updatedBeneficiaries[index] = {
				...updatedBeneficiaries[index],
				isBeneficialOwner: checked,
				participationPercentage: checked ? updatedBeneficiaries[index].participationPercentage : 0
			};
			return updatedBeneficiaries;
		});
		setErrors((prevState: any[]) => {
			const updatedErrors = [...prevState];
			updatedErrors[index || 0] = {
				...updatedErrors[index || 0],
				participationPercentage: false,
				percentageLesserThan25: false
			};
			return updatedErrors;
		});
	};

	const prefillFromInvestor = (index: number, investorIndex: number) => {
		const selectedInvestor = prefillOptions.find((option) => option.value === investorIndex)?.investor;
		if (!selectedInvestor) {
			return;
		}

		const addressParts = [
			selectedInvestor.mainResidence,
			selectedInvestor.mainResidencePostalCode,
			selectedInvestor.mainResidenceCity,
			selectedInvestor.mainResidenceCountry,
		].filter(Boolean);

		setBeneficiaries((prevState: Beneficiaries[]) => {
			const updatedBeneficiaries = [...prevState];
			updatedBeneficiaries[index] = {
				...updatedBeneficiaries[index],
				...mergeBeneficiaryWithInvestorData(updatedBeneficiaries[index], selectedInvestor),
			};
			return updatedBeneficiaries;
		});

		setTaxAddressInputs((prevState: string[]) => {
			const updatedInputs = [...prevState];
			updatedInputs[index] = addressParts.join(', ');
			return updatedInputs;
		});
	};

	return {
		loading,
		beneficiaries,
		errors,
		nationalitiesList,
		contractLanguage,
		contractId,
		taxAddressInputs,
		manualAddressVisibility,
		activeAddress,
		addRow,
		handleChangeData,
		handleSelectData,
		handleChangeDate,
		getInfo,
		deleteRow,
		saveData,
		handleSelectAddress,
		handleSearchAddress,
		onChangeHasPRP,
		onChangeIsBeneficialOwner,
		prefillOptions,
		prefillFromInvestor,
	};
};
